import React, { useState, useEffect } from "react";
import Header from "../../components/header/Header";
import { useCart } from "../../context/cart";
import Footer from "../../components/footer/Footer";
import { useAuth } from "../../context/auth";
import "../../css/CartPage.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";

const CartPage = () => {
  const { cart, clearCart, getCartTotal, decrementItem, getCartTT } = useCart();
  const [auth] = useAuth();
  const isLoggedIn = !!auth.token;
  const [productList, setProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const originalTax = (getCartTT() * 0.06).toFixed(2);
  const baseShippingCost = 7.00;
  const freeShippingThreshold = 50.00;

  // Free shipping for orders over $50
  const shippingCost = getCartTT() >= freeShippingThreshold ? 0 : baseShippingCost;

  const originalTotal = () => {
    if (getCartTT() > 0) {
      return (
          parseFloat((shippingCost + parseFloat(getCartTT())).toFixed(2)) +
          parseFloat(originalTax)
      ).toFixed(2);
    } else {
      return (parseFloat(getCartTT()) + parseFloat(originalTax)).toFixed(2);
    }
  };

  useEffect(() => {
    getProductList();
  }, [cart]);

  const apiUrl = `${process.env.REACT_APP_API}`;

  const getProductList = async () => {
    try {
      setIsLoading(true);
      const promises = cart.map(async (item) => {
        const res = await axios.get(`${apiUrl}/product/${item.product}`);
        const product = res.data;
        product.quantity = item.quantity;
        product.selectedSize = item.selectedSize;
        return product;
      });
      const products = await Promise.all(promises);

      // Filter out products that have 0 quantity available in the database
      const availableProducts = products.filter((product) => {
        if (!product.productSize || !product.productSize.sizes) return false;

        // If no sizes, product is available
        if (product.productSize.sizes.length === 0) return true;

        // Check if the selected size has quantity > 0
        const selectedSizeObj = product.productSize.sizes.find(
          (sizeObj) => sizeObj.label === product.selectedSize
        );

        // If size not found or quantity is 0, filter it out
        if (!selectedSizeObj || selectedSizeObj.quantity <= 0) {
          // Remove from cart
          decrementItem(product, product.selectedSize);
          toast.error(`${product.productName} (Size: ${product.selectedSize}) is out of stock and has been removed from your cart`);
          return false;
        }

        return true;
      });

      setProductList(availableProducts);
      setIsLoading(false);
    } catch (err) {
      console.error(err.message);
      setIsLoading(false);
    }
  };

  const handleDecrementItem = (item, size) => {
    decrementItem(item, size);
    toast.success("Removed from cart");
  };

  const handleClearCart = () => {
    if (window.confirm("Are you sure you want to clear your cart?")) {
      clearCart();
      toast.success("Cart cleared");
    }
  };

  if (cart.length === 0) {
    return (
        <>
          <Header />
          <div className="cart-page">
            <div className="container py-5">
              <div className="row justify-content-center">
                <div className="col-md-6">
                  <div className="empty-state-card text-center">
                    <RemoveShoppingCartIcon style={{ fontSize: "5rem", color: "#ccc" }} />
                    <h3 className="mt-4 mb-3">Your Cart is Empty</h3>
                    <p className="text-muted mb-4">
                      Looks like you haven't added anything to your cart yet.
                    </p>
                    <button
                        className="btn continue-shopping-button px-5"
                        onClick={() => navigate("/")}
                    >
                      Start Shopping
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </>
    );
  }

  return (
      <>
        <Header />
        <div className="cart-page">
          <div className="container py-5">
            {/* Page Header */}
            <div className="cart-header mb-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div>
                  <h1 className="cart-title">
                    <ShoppingCartIcon className="me-2" style={{ fontSize: "2.5rem" }} />
                    My Cart
                  </h1>
                  <p className="cart-subtitle">
                    {cart.length} {cart.length === 1 ? 'item' : 'items'} in your cart
                  </p>
                </div>
                <button className="btn clear-cart-button" onClick={handleClearCart}>
                  <DeleteOutlineIcon className="me-1" />
                  Clear Cart
                </button>
              </div>
            </div>

            <div className="row g-4">
              {/* Cart Items */}
              <div className="col-lg-8">
                <div className="cart-items-section">
                  {isLoading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: "#c70044" }} role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                  ) : (
                      productList.map((item, index) => (
                          <div key={index} className="cart-item-card">
                            <div className="row align-items-center">
                              {/* Product Image */}
                              <div className="col-md-3 col-4">
                                <Link to={`/products/${item._id}`}>
                                  <img
                                      src={item.images.front}
                                      alt={item.productName}
                                      className="cart-item-image"
                                  />
                                </Link>
                              </div>

                              {/* Product Details */}
                              <div className="col-md-6 col-8">
                                <Link
                                    to={`/products/${item._id}`}
                                    className="text-decoration-none"
                                >
                                  <h5 className="cart-item-name">{item.productName}</h5>
                                </Link>

                                <div className="cart-item-details mt-2">
                                  <span className="detail-badge me-2">Size: {item.selectedSize}</span>
                                  <span className="detail-badge">Qty: {item.quantity}</span>
                                </div>

                                <div className="cart-item-price mt-2">
                                  <span className="price-label">Unit Price: </span>
                                  <span className="price-value">
                              ${item.salePrice > 0 ? item.salePrice : item.productPrice}
                            </span>
                                </div>

                                <div className="cart-item-subtotal mt-2">
                                  <span className="subtotal-label">Subtotal: </span>
                                  <span className="subtotal-value">
                              ${(item.quantity * (item.salePrice > 0 ? item.salePrice : item.productPrice)).toFixed(2)}
                            </span>
                                </div>
                              </div>

                              {/* Remove Button */}
                              <div className="col-md-3 col-12 text-md-end text-start mt-3 mt-md-0">
                                <button
                                    className="btn remove-button"
                                    onClick={() => handleDecrementItem(item, item.selectedSize)}
                                >
                                  <DeleteOutlineIcon />
                                  <span className="ms-2">Remove</span>
                                </button>
                              </div>
                            </div>
                          </div>
                      ))
                  )}
                </div>

                {/* Continue Shopping */}
                <div className="text-center mt-4">
                  <Link to="/" className="continue-shopping-link">
                    ← Continue Shopping
                  </Link>
                </div>
              </div>

              {/* Order Summary */}
              <div className="col-lg-4">
                <div className="order-summary-card">
                  <h4 className="summary-title">
                    <ReceiptIcon className="me-2" />
                    Order Summary
                  </h4>

                  <div className="summary-divider"></div>

                  {/* Subtotal */}
                  <div className="summary-row">
                    <span className="summary-label">Subtotal:</span>
                    <span className="summary-value">${getCartTT()}</span>
                  </div>

                  {/* Taxes */}
                  <div className="summary-row">
                    <span className="summary-label">Taxes (6%):</span>
                    <span className="summary-value">${originalTax}</span>
                  </div>

                  {/* Shipping */}
                  {getCartTT() > 0 && (
                      <>
                        <div className="summary-row">
                          <span className="summary-label">
                            <LocalShippingIcon fontSize="small" className="me-1" />
                            Shipping:
                          </span>
                          <span className="summary-value" style={{
                            color: shippingCost === 0 ? '#28a745' : 'inherit',
                            fontWeight: shippingCost === 0 ? '600' : 'normal'
                          }}>
                            {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                          </span>
                        </div>

                        {/* Free Shipping Message */}
                        {shippingCost === 0 ? (
                          <div className="alert alert-success mt-2 mb-2 py-2 px-3" style={{ fontSize: '0.875rem' }}>
                            <LocalShippingIcon fontSize="small" className="me-1" />
                            You've qualified for FREE shipping!
                          </div>
                        ) : (
                          <div className="alert alert-info mt-2 mb-2 py-2 px-3" style={{ fontSize: '0.875rem' }}>
                            <LocalShippingIcon fontSize="small" className="me-1" />
                            Add ${(freeShippingThreshold - getCartTT()).toFixed(2)} more for FREE shipping!
                          </div>
                        )}
                      </>
                  )}

                  <div className="summary-divider"></div>

                  {/* Grand Total */}
                  <div className="summary-total">
                    <span className="total-label">Grand Total:</span>
                    <span className="total-value">
                    ${getCartTT() > 0
                        ? (shippingCost + parseFloat(getCartTotal())).toFixed(2)
                        : getCartTotal()}
                  </span>
                  </div>

                  <p className="summary-note text-center">
                    <small>(includes taxes & shipping)</small>
                  </p>

                  {/* Checkout Button */}
                  <Link to="/cart/checkout" className="d-block">
                    <button className="btn checkout-button w-100">
                      Proceed to Checkout
                    </button>
                  </Link>

                  {/* Security Badge */}
                  <div className="security-badge text-center mt-3">
                    <small className="text-muted">
                      🔒 Secure Checkout
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
  );
};

export default CartPage;